import { useEffect, useState } from "react";
import { Button, Card, Input, Select, Space, Text, useToast } from "@app/ui";
import { updatePassportLineage } from "../api/dpp.js";
import { useT } from "../i18n.js";
import { LINEAGE_REASONS } from "../types.js";
import type { LineageReason, PassportDetail } from "../types.js";

/** "id:<uuid>" names a same-org original; anything else is a URI; blank = none. */
function savedOriginal(p: PassportDetail): string {
  return p.originalPassportUri ?? (p.originalPassportId ? `id:${p.originalPassportId}` : "");
}

/**
 * Lineage (draft TR ESPR Art. 13(1)(ç)): a passport for a refurbished /
 * remanufactured / repurposed unit links back to the original. Owns its draft,
 * which resets whenever the saved values change.
 */
export function PassportLineageCard({
  orgId,
  passport,
  canWrite,
  onSaved,
  onError,
}: {
  orgId: string;
  passport: PassportDetail;
  canWrite: boolean;
  onSaved: () => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [originalUri, setOriginalUri] = useState(savedOriginal(passport));
  const [lineageReason, setLineageReason] = useState<LineageReason | "">(passport.lineageReason ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOriginalUri(savedOriginal(passport));
    setLineageReason(passport.lineageReason ?? "");
  }, [passport.originalPassportUri, passport.originalPassportId, passport.lineageReason]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = originalUri !== savedOriginal(passport) || (lineageReason || null) !== passport.lineageReason;

  const save = async () => {
    setSaving(true);
    try {
      const raw = originalUri.trim();
      await updatePassportLineage(orgId, passport.id, {
        originalPassportId: raw.startsWith("id:") ? raw.slice(3) : null,
        originalPassportUri: raw && !raw.startsWith("id:") ? raw : null,
        lineageReason: raw ? lineageReason || null : null,
      });
      toast.success(t("passport.lineageSaved"));
      onSaved();
    } catch (e) {
      onError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t("passport.lineage")}
      extra={
        canWrite ? (
          <Button size="sm" loading={saving} disabled={!dirty} onClick={() => void save()}>
            {t("common.save")}
          </Button>
        ) : undefined
      }
    >
      <Space direction="vertical" size="sm" style={{ width: "100%" }}>
        <Text small secondary>
          {t("passport.lineageHint")}
        </Text>
        <Space wrap>
          <Input
            label={t("passport.originalPassport")}
            placeholder="https://… or id:<uuid>"
            disabled={!canWrite}
            value={originalUri}
            onChange={(e) => setOriginalUri(e.target.value)}
          />
          <div style={{ width: 220 }}>
            <Select
              label={t("passport.lineageReason")}
              disabled={!canWrite}
              options={[
                { label: "—", value: "" },
                ...LINEAGE_REASONS.map((r) => ({ label: t(`lineageReason.${r}`), value: r })),
              ]}
              value={lineageReason}
              onChange={(v) => setLineageReason(v as LineageReason | "")}
            />
          </div>
        </Space>
      </Space>
    </Card>
  );
}
